import { Grid, SelectChangeEvent, useEventCallback } from '@mui/material';
import { memo } from 'react';
import { BewareFormVM } from 'mutation/view-models/registration-beware';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { noop } from 'shell/utils/noop';
import TypologyBewareList from '../typology-beware-list';

interface FormBewareProps {
  values?: BewareFormVM | null;
  onChange: (key: keyof BewareFormVM, value: BewareFormVM[keyof BewareFormVM]) => void;
  errors?: FormikErrors<BewareFormVM>;
  touched?: FormikTouched<BewareFormVM>;
  isEdit?: boolean;
  disabled?: boolean;
}

const FormBewareComponent = ({
  values,
  onChange,
  errors,
  touched,
  isEdit = false,
  disabled = false,
}: FormBewareProps): React.ReactElement => {
  const onChangeMunicipality = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    onChange('municipalityItalian', municipality);
  });

  const onChangeTypologyBewareList = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('typology', event.target.value);
  });

  const { userProfile } = useUserProfile();

  const isEditDiffida = userProfile.datiLogin?.profile?.feature?.diffida === '2';

  const disabledBeware = disabled || !isEditDiffida;

  return (
    <Grid item container spacing={2}>
      {isEdit ? undefined : (
        <Grid item xs={12} md={6}>
          <TypologyBewareList
            onChange={onChangeTypologyBewareList}
            value={values?.typology || ''}
            disabled={disabledBeware}
            error={touched?.typology && Boolean(errors?.typology)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        É diffidato dal trasferire la propria residenza nel comune di
      </Grid>
      <Grid item xs={12} md={5}>
        <MunicipalityList
          value={values?.municipalityItalian || null}
          onChange={onChangeMunicipality}
          label="Comune *"
          id="municipalityItalian"
          disabled={disabledBeware}
          error={touched?.note && Boolean(errors?.municipalityItalian)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          id="province"
          label="Provincia"
          aria-label="Provincia"
          disabled
          fullWidth
          onChange={noop}
          value={values?.municipalityItalian?.province || ''}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <DatePicker
          label="Dal giorno *"
          aria-label="Dal giorno"
          value={values?.dateForm || null}
          onChange={(date) => {
            onChange('dateForm', date);
          }}
          disabled={disabledBeware}
          renderInput={(params) => (
            <PmiTextField
              id="dateForm"
              {...params}
              fullWidth
              error={touched?.dateForm && Boolean(errors?.dateForm)}
              helperText={errors?.dateForm}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <PmiTextField
          label="Per anni *"
          aria-label="Per anni"
          fullWidth
          onChange={(event) => {
            onChange('year', event?.target.value);
          }}
          id="year"
          value={values?.year || ''}
          type="number"
          disabled={disabledBeware}
          error={touched?.year && Boolean(errors?.year)}
          helperText={errors?.year}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          label="Note *"
          aria-label="Note"
          id="note"
          name="note"
          fullWidth
          multiline
          rows={5}
          inputProps={{
            maxLength: '240',
          }}
          value={values?.note || ''}
          onChange={(event) => onChange('note', event?.target.value)}
          disabled={disabledBeware}
          error={touched?.note && Boolean(errors?.note)}
          helperText={errors?.note}
        />
      </Grid>
    </Grid>
  );
};

FormBewareComponent.displayName = 'Form Beware Component';

const FormBeware = memo(FormBewareComponent);

export default FormBeware;
